import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getTurnConnections, getTurnMetrics, getNodes } from '../services/api.service';
import { PageTitle } from '@trinetra/shared';
import TurnMetricsChart from '../components/TurnMetricsChart';

const POLL_INTERVAL = 10_000; // 10s (reduced from 5s to lower cloud-BE load)

/* ── helpers ────────────────────────────────────── */
function fmtUptime(sec: number): string {
  if (!sec || sec < 0) return '—';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

function fmtBytes(b: number): string {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtNum(n: number): string {
  return (n || 0).toLocaleString();
}

/* ── small components ───────────────────────────── */
function StatCard({ label, value, sub, color = 'text-foreground' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-card">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

/* ── expandable session row ──────────────────────── */
function SessionRow({ conn }: { conn: any }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-muted/20 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Session ID */}
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className={`text-muted-foreground text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
            <div>
              <div className="font-mono text-xs text-foreground">{conn.sessionId.slice(0, 12)}…</div>
              <div className="text-[10px] text-muted-foreground">{conn.userId.slice(0, 16)}</div>
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="text-center px-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
            conn.state === 'connected' ? 'bg-emerald-500/20 text-emerald-400' :
            conn.state === 'connecting' ? 'bg-amber-500/20 text-amber-400' :
            conn.state === 'disconnected' ? 'bg-red-500/20 text-red-400' :
            'bg-blue-500/20 text-blue-400'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              conn.state === 'connected' ? 'bg-emerald-400 animate-pulse' :
              conn.state === 'connecting' ? 'bg-amber-400 animate-pulse' :
              conn.state === 'disconnected' ? 'bg-red-400' :
              'bg-blue-400'
            }`} />
            {conn.state}
          </span>
        </td>

        {/* Connection Type */}
        <td className="text-center px-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
            conn.connectionType === 'relay' ? 'bg-purple-500/20 text-purple-400' :
            conn.connectionType === 'direct' ? 'bg-emerald-500/20 text-emerald-400' :
            'bg-muted text-muted-foreground'
          }`}>
            {conn.connectionType === 'relay' ? '🔀 TURN' :
             conn.connectionType === 'direct' ? '⚡ Direct' : '—'}
          </span>
        </td>

        {/* Uptime */}
        <td className="text-center px-3">
          <span className="font-mono text-xs text-muted-foreground">{fmtUptime(conn.uptimeSeconds)}</span>
        </td>

        {/* TURN Bandwidth ↓/↑ (symmetric relay: recv from SFU = send to viewer) */}
        <td className="text-center px-3">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1">
              <span className={`font-mono text-xs font-bold ${conn.bandwidthMbps > 0 ? 'text-cyan-400' : 'text-muted-foreground/40'}`}>
                {conn.bandwidthMbps > 0 ? conn.bandwidthMbps.toFixed(2) : '0'}
              </span>
              <span className="text-[9px] text-muted-foreground">↓↑</span>
            </div>
            <span className="text-[9px] text-muted-foreground">Mbps</span>
          </div>
        </td>

        {/* Packets/sec ↓/↑ */}
        <td className="text-center px-3">
          <div className="flex flex-col items-center">
            <span className="font-mono text-xs text-muted-foreground">
              {fmtNum(conn.pktsPerSec)}/s
            </span>
            <span className="text-[9px] text-muted-foreground">↓↑</span>
          </div>
        </td>

        {/* Tracks */}
        <td className="text-center px-3">
          <span className="font-mono text-xs text-muted-foreground">
            {conn.liveTracks || 0}
            {conn.playbackTracks > 0 && <span className="text-amber-400"> +{conn.playbackTracks}p</span>}
          </span>
        </td>

        {/* NACK/PLI */}
        <td className="text-center px-3">
          <div className="flex flex-col items-center text-[10px] font-mono">
            <span className={conn.nackCount > 0 ? 'text-amber-400' : 'text-muted-foreground/40'}>
              {fmtNum(conn.nackCount)} N
            </span>
            <span className={conn.pliCount > 0 ? 'text-amber-400' : 'text-muted-foreground/40'}>
              {fmtNum(conn.pliCount)} P
            </span>
          </div>
        </td>

        {/* Total Data */}
        <td className="text-right px-4">
          <span className="font-mono text-xs text-muted-foreground">
            {fmtBytes(conn.bytesSent)}
          </span>
        </td>
      </tr>

      {/* Expanded detail */}
      {expanded && (
        <tr>
          <td colSpan={9} className="bg-muted/10 px-6 py-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[11px] font-mono">
              <div>
                <span className="text-muted-foreground">Session ID:</span>{' '}
                <span className="text-foreground break-all">{conn.sessionId}</span>
              </div>
              <div>
                <span className="text-muted-foreground">User ID:</span>{' '}
                <span className="text-foreground break-all">{conn.userId}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Connection:</span>{' '}
                <span className={conn.connectionType === 'relay' ? 'text-purple-400' : conn.connectionType === 'direct' ? 'text-emerald-400' : 'text-muted-foreground'}>
                  {conn.connectionType === 'relay' ? 'TURN Relay' : conn.connectionType === 'direct' ? 'Direct (P2P)' : 'Pending'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Connected:</span>{' '}
                <span className="text-foreground">{new Date(conn.connectedAt).toLocaleTimeString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Remote:</span>{' '}
                <span className="text-foreground">{conn.remoteAddr || '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Tracks:</span>{' '}
                <span className="text-foreground">{conn.liveTracks || 0} live, {conn.playbackTracks || 0} playback</span>
              </div>
              <div>
                <span className="text-muted-foreground">TURN Recv (SFU→TURN):</span>{' '}
                <span className="text-cyan-400">{conn.bandwidthMbps?.toFixed(3) || '0'} Mbps · {fmtNum(conn.pktsPerSec || 0)} pkt/s</span>
              </div>
              <div>
                <span className="text-muted-foreground">TURN Send (TURN→Viewer):</span>{' '}
                <span className="text-purple-400">{conn.bandwidthMbps?.toFixed(3) || '0'} Mbps · {fmtNum(conn.pktsPerSec || 0)} pkt/s</span>
              </div>
              <div>
                <span className="text-muted-foreground">NACK/s · PLI/s:</span>{' '}
                <span className={conn.nackPerSec > 1 || conn.pliPerSec > 0.5 ? 'text-amber-400' : 'text-foreground'}>
                  {conn.nackPerSec || 0} · {conn.pliPerSec || 0}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Packets:</span>{' '}
                <span className="text-foreground">{fmtNum(conn.packetsSent)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Data:</span>{' '}
                <span className="text-foreground">{fmtBytes(conn.bytesSent)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Avg Bytes/Pkt:</span>{' '}
                <span className="text-foreground">{conn.packetsSent > 0 ? Math.round(conn.bytesSent / conn.packetsSent) : 0} B</span>
              </div>
              <div>
                <span className="text-muted-foreground">NACK Total / PLI Total:</span>{' '}
                <span className="text-foreground">{fmtNum(conn.nackCount)} / {fmtNum(conn.pliCount)}</span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── main page ──────────────────────────────────── */
export default function TurnMonitor() {
  const [data, setData] = useState<any>(null);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [nodeNames, setNodeNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [connData, metricsData] = await Promise.all([
        getTurnConnections(),
        getTurnMetrics(timeRange),
      ]);
      setData(connData);
      setMetrics(metricsData.metrics || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch TURN data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch node names once
  useEffect(() => {
    getNodes().then((data: any) => {
      const map: Record<string, string> = {};
      for (const n of data?.nodes || []) map[n.nodeId] = n.name;
      setNodeNames(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let inFlight = false;
    const safeFetch = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try { await fetchData(); } finally { inFlight = false; }
    };

    safeFetch();
    timerRef.current = setInterval(safeFetch, POLL_INTERVAL);

    const onVisibility = () => { if (!document.hidden) safeFetch(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  const summary = data?.summary || {};
  const byNode: Record<string, any[]> = data?.byNode || {};
  const connections: any[] = data?.connections || [];
  const totalNack = connections.reduce((s: number, c: any) => s + (c.nackCount || 0), 0);
  const totalPli = connections.reduce((s: number, c: any) => s + (c.pliCount || 0), 0);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PageTitle title="TURN Server" subtitle="Real-time relay traffic — sessions grouped by local node">
        {/* TURN reachability */}
        <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
          <span className={`w-1.5 h-1.5 rounded-full ${summary.activeSessions > 0 || summary.uptimeSeconds > 0 ? 'bg-emerald-400' : 'bg-red-400'}`} />
          TURN {summary.uptimeSeconds > 0 ? '✓ running' : '✗ unreachable'}
          {summary.uptimeSeconds > 0 && (
            <span className="ml-1">· uptime {fmtUptime(summary.uptimeSeconds)}</span>
          )}
        </div>
      </PageTitle>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          ⚠ {error}
        </div>
      )}

      {/* ── Total Bandwidth Banner ──────────────────── */}
      {connections.length > 0 ? (
        <div className="rounded-xl border border-brand/30 bg-brand/5 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-3xl font-bold font-mono text-brand">
                {(summary.bandwidthInMbps || 0).toFixed(2)} <span className="text-lg text-brand/70">↓</span>
                {' / '}
                {(summary.bandwidthOutMbps || 0).toFixed(2)} <span className="text-lg text-brand/70">↑ Mbps</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                TURN Server Load · {connections.length} active stream{connections.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-[11px] font-mono">
            <div className="text-center">
              <div className="text-cyan-400 font-bold text-lg">{fmtNum(summary.pktsInPerSec || 0)}/s</div>
              <div className="text-muted-foreground">Pkts Recv ({fmtNum(summary.totalPktsIn || 0)} total)</div>
            </div>
            <div className="text-center">
              <div className="text-purple-400 font-bold text-lg">{fmtNum(summary.pktsOutPerSec || 0)}/s</div>
              <div className="text-muted-foreground">Pkts Sent ({fmtNum(summary.totalPktsOut || 0)} total)</div>
            </div>
            <div className="text-center">
              <div className="text-cyan-400 font-bold text-lg">{fmtBytes(summary.totalBytesIn || 0)}</div>
              <div className="text-muted-foreground">Data In</div>
            </div>
            <div className="text-center">
              <div className="text-purple-400 font-bold text-lg">{fmtBytes(summary.totalBytesOut || 0)}</div>
              <div className="text-muted-foreground">Data Out</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center justify-center gap-3">
          <div className="text-center">
            <div className="text-2xl font-bold font-mono text-muted-foreground/40">Idle</div>
            <div className="text-[11px] text-muted-foreground">No active streams — TURN server standing by</div>
          </div>
        </div>
      )}

      {/* ── Summary Cards ───────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          label="Active Viewers"
          value={connections.length}
          color={connections.length > 0 ? 'text-emerald-400' : 'text-muted-foreground/40'}
          sub={connections.length > 0 ? `across ${summary.nodeCount || 0} node${(summary.nodeCount || 0) !== 1 ? 's' : ''}` : 'no active streams'}
        />
        <StatCard
          label="TURN Allocations"
          value={summary.activeSessions || 0}
          color={summary.activeSessions > 0 ? 'text-blue-400' : 'text-muted-foreground/40'}
          sub="coturn port reservations"
        />
        <StatCard
          label="Bandwidth In"
          value={connections.length > 0 ? `${(summary.bandwidthInMbps || 0).toFixed(1)}` : '—'}
          color={connections.length > 0 ? 'text-cyan-400' : 'text-muted-foreground/40'}
          sub={connections.length > 0 ? 'Mbps (recv)' : 'no streams'}
        />
        <StatCard
          label="Bandwidth Out"
          value={connections.length > 0 ? `${(summary.bandwidthOutMbps || 0).toFixed(1)}` : '—'}
          color={connections.length > 0 ? 'text-purple-400' : 'text-muted-foreground/40'}
          sub={connections.length > 0 ? 'Mbps (sent)' : 'no streams'}
        />
        <StatCard
          label="Peak Connections"
          value={summary.peakConnections || 0}
          color="text-amber-400"
          sub="all-time high"
        />
        <StatCard
          label="Errors"
          value={summary.errors || 0}
          color={summary.errors > 0 ? 'text-red-400' : 'text-muted-foreground/40'}
          sub="STUN binding errors"
        />
      </div>

      {/* ── Metrics History Chart ───────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <span className="text-brand">●</span> Metrics History
          </h2>
          <div className="flex gap-1">
            {[30, 60, 180, 720, 1440].map(mins => (
              <button
                key={mins}
                onClick={() => setTimeRange(mins)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                  timeRange === mins ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {mins < 60 ? `${mins}m` : `${mins / 60}h`}
              </button>
            ))}
          </div>
        </div>
        <TurnMetricsChart data={metrics} />
      </div>

      {/* ── Connections by Node ─────────────────────── */}
      {Object.keys(byNode).length > 0 ? (
        Object.entries(byNode).map(([nodeId, nodeConns]) => {
          const nodeName = nodeNames[nodeId] || nodeId;
          const nodeTotal = nodeConns.reduce((s: number, c: any) => s + (c.bandwidthMbps || 0), 0);
          return (
            <div key={nodeId}>
              <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="text-blue-400">●</span>
                {nodeName}
                <span className="text-[10px] font-normal text-muted-foreground">
                  ({nodeConns.length} session{nodeConns.length !== 1 ? 's' : ''})
                </span>
                <span className="ml-auto text-[11px] font-mono text-cyan-400">
                  {nodeTotal.toFixed(3)} Mbps ↓↑
                </span>
              </h2>
              <div className="rounded-xl border border-border bg-card overflow-hidden shadow-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Session</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Conn</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Uptime</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">BW ↓↑</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Pkts ↓↑</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Tracks</th>
                      <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">NACK/PLI</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Data Sent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {nodeConns.map((c: any) => (
                      <SessionRow key={c.sessionId} conn={c} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      ) : connections.length === 0 ? (
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <span className="text-blue-400">●</span> Active Connections
          </h2>
          <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground shadow-card">
            No active TURN connections
          </div>
        </div>
      ) : null}

      {/* ── Quality Summary ─────────────────────────── */}
      {connections.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-3 shadow-card">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-[11px] font-mono text-muted-foreground">
            <span>
              Total Sent: <span className="text-brand font-bold">{fmtBytes(summary.sfuTotalBytesSent || 0)}</span>
              {' · '}{fmtNum(summary.sfuTotalPktsSent || 0)} pkts
            </span>
            <span>
              Per-Viewer Avg: <span className="text-foreground font-bold">
                {connections.length > 0
                  ? ((summary.sfuTotalBytesSent || 0) / connections.length / 1024 / 1024).toFixed(1)
                  : '0'} MB
              </span>
            </span>
            <span>
              Total NACK: <span className={totalNack > 0 ? 'text-amber-400 font-bold' : 'text-muted-foreground/40'}>
                {fmtNum(totalNack)}
              </span>
              {' · PLI: '}
              <span className={totalPli > 0 ? 'text-amber-400 font-bold' : 'text-muted-foreground/40'}>
                {fmtNum(totalPli)}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Auto-refresh indicator */}
      <div className="text-[10px] text-muted-foreground/50 text-center font-mono">
        Auto-refreshing every {POLL_INTERVAL / 1000}s
      </div>
    </div>
  );
}
